import { atom, selector } from 'recoil'
import { keyFactory, localStorageEffect } from 'src/utils/recoil'
import * as defaults from 'src/utils/default-colors'
import { TradeAreaType, type } from 'src/atoms/trade-area'

const settingKey = keyFactory('settings-colors')

const gradientsAmount = atom<number>({
  key: settingKey('gradients-amount'),
  default: defaults.gradientsAmount,
})

const gradientsLevels = atom<number>({
  key: settingKey('gradients-levels'),
  default: defaults.gradientsLevels,
})

const gradientsDistributionList = atom<number[]>({
  key: settingKey('gradients-distribution-list'),
  default: defaults.gradientsDistributionList,
})

const gradientsDistribution = selector<number[]>({
  key: settingKey('gradients-distribution'),
  get: ({ get }) => {
    const gradientsLevelsValue = get(gradientsLevels)
    const gradientsDistributionListValue = get(gradientsDistributionList)

    return Array.from(Array(gradientsLevelsValue - 1).keys()).map((index) => {
      return gradientsDistributionListValue[index] || 99
    })
  },
})

export const gradientsDistributionPercentages = selector<number[]>({
  key: settingKey('gradients-distribution-percentages'),
  get: ({ get }) => {
    const gradientsDistributionValue = get(gradientsDistribution)
    return [0, ...gradientsDistributionValue.map((dist) => dist / 100), 1]
  },
})

export const gradientsList = selector<string[][]>({
  key: settingKey('gradients-list'),
  get: ({ get }) => {
    const tradeAreaType = get(type)
    if (tradeAreaType !== TradeAreaType.FOOT_TRAFFIC && tradeAreaType !== TradeAreaType.CUSTOM_POLYGON) {
      return defaults.gradientsListContrast
    }

    return defaults.gradientsList
  },
})

export const gradientsColors = selector<string[][]>({
  key: settingKey('gradients-colors'),
  get: ({ get }) => {
    const gradientsAmountValue = get(gradientsAmount)
    const gradientsLevelsValue = get(gradientsLevels)
    const gradientsListValue = get(gradientsList)

    return Array.from(Array(gradientsAmountValue).keys()).map((index) => {
      return Array.from(Array(gradientsLevelsValue).keys()).map((level) => {
        return gradientsListValue[index]?.[level] || defaults.gradientsDefaultColor
      })
    })
  },
})

export const gradientsTopColors = selector<string[]>({
  key: settingKey('gradients-top-colors'),
  get: ({ get }) => {
    const gradientsColorsValue = get(gradientsColors)
    const gradientsLevelsValue = get(gradientsLevels)
    return gradientsColorsValue.map((index) => {
      return index[gradientsLevelsValue - 1]
    })
  },
})

export const gradientsColorsWithTransparent = selector<string[][]>({
  key: settingKey('gradients-colors-with-transparent'),
  get: ({ get }) => {
    const gradientsColorsValue = get(gradientsColors)
    const gradientsDistributionPercentagesValue = get(gradientsDistributionPercentages)

    return gradientsColorsValue.map((gradient) => {
      return Array.from(Array(100).keys()).map((index) => {
        if (index === 0) {
          return defaults.gradientsDefaultTransparent
        }
        const percentage = index / 100
        const gradientIndex = gradientsDistributionPercentagesValue.findIndex((_dist, distIndex) => {
          return (
            percentage >= gradientsDistributionPercentagesValue[distIndex] &&
            percentage <= gradientsDistributionPercentagesValue[distIndex + 1]
          )
        })
        return gradient[gradientIndex]
      })
    })
  },
})

export const gradientsDefaultColor = selector<string>({
  key: settingKey('gradients-default-color'),
  get: ({ get }) => {
    return defaults.gradientsDefaultColor
  },
})

export const gradientsDefaultTransparent = selector<string>({
  key: settingKey('gradients-default-transparent'),
  get: ({ get }) => {
    return defaults.gradientsDefaultTransparent
  },
})

export const hexagonMapOpacity = atom<number>({
  key: settingKey('hexagon-map-opacity'),
  default: defaults.hexagonMapOpacity,
})

export const userSavedColors = atom<string[]>({
  key: settingKey('color-palette'),
  default: [],
  effects: [localStorageEffect('user-color-palette')],
})
